import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'
import { useWorkspace2Store } from '../../../Workspace2Store'
import { nullOrUndefinedResolver } from '../../../utils'
import { PromoteDemoteState, SetContentItemToPromoteDemote } from '../types'
import { findContentItemDepth } from '../utils/depthCalculator'
import { validateEligibility } from '../utils/validators'

/**
 * Default state for selection-related fields only.
 * isMutationInFlight is intentionally excluded — it is lifecycle-independent of item selection
 * and must NOT be cleared by resetPromoteDemoteState or setContentItemToPromoteDemote.
 * Only setMutationInFlight (called in successCbFn / errorCbFn) controls that flag.
 */
const defaultPromoteDemoteState = {
    contentItemToPromoteDemote: null,
    isInProject: false,
    isUserAdded: false,
    isL1Paragraph: false,
    isArticle: false,
    paragraphLevel: 0,
    depth: 0,
}

export const usePromoteDemoteStore = create<PromoteDemoteState>((set, get) => ({
    ...defaultPromoteDemoteState,
    isMutationInFlight: false,
    canBePromoted: (isContentTeamFromStudioPage: boolean = false) => {
        const state = get()
        const options = {
            contentItemToPromoteDemote: state.contentItemToPromoteDemote,
            isInProject: state.isInProject,
            isUserAdded: state.isUserAdded,
            isL1Paragraph: state.isL1Paragraph,
            isArticle: state.isArticle,
            paragraphLevel: state.paragraphLevel,
            depth: state.depth,
            isContentTeamFromStudioPage,
        }
        const { canBePromoted } = validateEligibility(options)

        return canBePromoted
    },

    canBeDemoted: (isContentTeamFromStudioPage: boolean = false) => {
        const state = get()
        const options = {
            contentItemToPromoteDemote: state.contentItemToPromoteDemote,
            isInProject: state.isInProject,
            isUserAdded: state.isUserAdded,
            isL1Paragraph: state.isL1Paragraph,
            isArticle: state.isArticle,
            paragraphLevel: state.paragraphLevel,
            depth: state.depth,
            isContentTeamFromStudioPage,
        }
        const { canBeDemoted } = validateEligibility(options)

        return canBeDemoted
    },

    setMutationInFlight: (value: boolean) => set({ isMutationInFlight: value }),

    resetPromoteDemoteState: () => set(defaultPromoteDemoteState),

    // Action to update the state based on content analysis
    setContentItemToPromoteDemote: (
        props: Partial<SetContentItemToPromoteDemote> | null
    ) => {
        if (!props || !props.contentItemViewNodes?.length) {
            // reset state if no content item or nodes are provided
            set(defaultPromoteDemoteState)
            return
        }

        const {
            contentItemViewNodes,
            contentItemToPromoteDemote,
            isInProject,
        } = nullOrUndefinedResolver(props)

        const treeData = useWorkspace2Store.getState().treeData
        // calculate depth ONCE here
        const depth = contentItemToPromoteDemote.id
            ? findContentItemDepth(contentItemToPromoteDemote.id, treeData)
            : 0

        // find the content item
        const content = contentItemViewNodes?.find((node) =>
            node.elements?.some(
                (element) => element.id === contentItemToPromoteDemote.id
            )
        )

        // extract content required properties for evaluation
        const paragraphLevel = content?.order || 0
        const isArticle = content?.textType === 'Article'
        const isUserAdded = content?.isUserAdded || false
        // determine if content is an L1 paragraph
        const isL1Paragraph = Boolean(
            content &&
                contentItemViewNodes?.some((node) => {
                    if (content.dtvContentItemParentId) {
                        return (
                            node.id === content.dtvContentItemParentId &&
                            node.textType === 'Article'
                        )
                    }
                    return (
                        node.dtvItemId === content.dtvParentId &&
                        node.textType === 'Article'
                    )
                })
        )

        // update store state with evaluated properties
        set({
            contentItemToPromoteDemote,
            isInProject,
            isUserAdded,
            isL1Paragraph,
            isArticle,
            paragraphLevel,
            depth: depth >= 0 ? depth : 0, // use calculated depth, fallback to 0
        })
    },
}))

/**
 * A custom hook that retrieves a shallowly selected value from the `usePromoteDemoteStore` store.
 * Sample usage:
 * ```typescript
 * const { contentItemToPromoteDemote, setContentItemToPromoteDemote } = usePromoteDemoteSelector((state) => ({
 *   contentItemToPromoteDemote: state.contentItemToPromoteDemote,
 *   setContentItemToPromoteDemote: state.setContentItemToPromoteDemote,
 * }));
 * ```
 *
 * @param selector - A function that takes the store state and returns the value to be selected.
 * @returns The selected value from the store.
 */
export const usePromoteDemoteSelector = <T>(
    selector: (state: PromoteDemoteState) => T
): T => usePromoteDemoteStore(useShallow(selector))
